import { type ComponentProps, useMemo } from 'react'

import { InputSelect, InputSelectWithLabel } from '@/components/common/Input'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { GALLERY_GROUPBY_LABELS, type GalleryGroupbySlugType } from '@/defs/gallery'
import { galleryActions } from '@/store/gallery'
import { useReduxDispatch } from '@/store/hooks'
import { toastActions } from '@/store/toast'
import { trackEvent } from '@/utils/analyticsUtil'
import { scrollToAnchor } from '@/utils/scrollUtil'

import { useGalleryDisplay } from '../../GalleryDisplayProvider/GalleryDisplayProvider'
import { useGalleryPreload } from '../../GalleryPreloadProvider/GalleryPreloadProvider'
import { GalleryControlsLockable } from '../GalleryControlsLockable/GalleryControlsLockable'
import { GalleryControlsPriceAlert } from '../GalleryControlsPriceAlert/GalleryControlsPriceAlert'

type Props = {
  variant?: ComponentProps<typeof InputSelectWithLabel>['variant']
}

export const GalleryControlsGroupby = ({ variant }: Props) => {
  const dispatch = useReduxDispatch()
  const { groupby, groupbyDefault, groupbySlugs } = useGalleryDisplay()
  const { startPreload } = useGalleryPreload()

  const options = useMemo(
    () =>
      groupbySlugs.map((slug) => ({
        label: (
          <IconWithLabel iconBefore={GALLERY_GROUPBY_LABELS[slug].icon}>
            {GALLERY_GROUPBY_LABELS[slug].label}
          </IconWithLabel>
        ),
        value: slug,
      })),
    [groupbySlugs],
  )

  const onSelect = (v: GalleryGroupbySlugType | 'reset') => {
    if (v !== groupby) requestAnimationFrame(() => scrollToAnchor({ id: 'gallery', ifNearTop: true, isSmooth: true }))

    if (v === 'reset') {
      dispatch(galleryActions.setLockedPrefSlug({ isLocked: false, slug: 'groupby' }))
      dispatch(galleryActions.setGroupby(groupbyDefault))
      return
    }

    if (v === 'price') {
      dispatch(toastActions.add(<GalleryControlsPriceAlert variant='groupby' />))
    }

    dispatch(galleryActions.setGroupby(v))
    trackEvent({ action: 'group_by_submit', category: 'cardlists', label: v })
  }

  return (
    <GalleryControlsLockable editedKeys={['groupby']} onPointerEnter={startPreload} slug='groupby'>
      <InputSelect
        onSelect={onSelect}
        options={options}
        reset={{ label: 'Reset', value: 'reset' }}
        size='sm'
        value={groupby}
      >
        <InputSelectWithLabel label='Group by' options={options} size='sm' value={groupby} variant={variant} />
      </InputSelect>
    </GalleryControlsLockable>
  )
}
